import React from 'react';
import { useAbout } from '../hooks/useContentful';

const AboutSection = () => {
  const { content, loading, error } = useAbout();

  if (loading) {
    return (
      <section id="nosotros" className="py-12 md:py-20 bg-white relative">
        {/* Section Separator */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-purple-300 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-12 items-center">
            {/* Image Skeleton */}
            <div className="mb-10 lg:mb-0">
              <div className="bg-gradient-to-br from-purple-200 via-purple-300 to-purple-200 rounded-3xl h-96 w-full animate-shimmer-purple shadow-lg"></div>
            </div>
            {/* Text Skeleton */}
            <div>
              <div className="h-8 bg-gradient-to-r from-purple-300 to-purple-400 rounded-lg w-3/4 mb-6 animate-shimmer-purple"></div>
              <div className="space-y-3 mb-6">
                <div className="h-4 bg-gradient-to-r from-purple-200 to-purple-300 rounded animate-shimmer-purple"></div>
                <div className="h-4 bg-gradient-to-r from-purple-200 to-purple-300 rounded animate-shimmer-purple"></div>
                <div className="h-4 bg-gradient-to-r from-purple-200 to-purple-300 rounded w-5/6 animate-shimmer-purple"></div>
              </div>
              <div className="space-y-4">
                {[...Array(3)].map((_, index) => (
                  <div key={index} className="flex items-start" style={{ animationDelay: `${index * 0.1}s` }}>
                    <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-purple-500 rounded-full flex-shrink-0 mt-1 animate-glow"></div>
                    <div className="ml-3 space-y-2 flex-1">
                      <div className="h-4 bg-gradient-to-r from-purple-200 to-purple-300 rounded w-full animate-shimmer-purple"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error || !content) {
    return null;
  }

  return (
    <section id="nosotros" className="py-16 md:py-24 bg-gradient-to-br from-purple-50 via-white to-pink-50 relative overflow-hidden">
      {/* Section Separator */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-purple-400 to-pink-400 to-transparent"></div>
      
      {/* Decorative elements */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-purple-200/20 rounded-full blur-3xl animate-float"></div>
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-pink-200/15 rounded-full blur-3xl animate-float" style={{ animationDelay: '1.5s' }}></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="lg:grid lg:grid-cols-2 lg:gap-12 items-center">
          
          {/* Imagen */}
          <div className="mb-8 lg:mb-0 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            <img
              src={content.image?.fields?.file?.url}
              alt="Imagen sección sobre nosotros"
              className="transform hover:scale-105 transition-all duration-500 rounded-2xl w-full shadow-lg hover:shadow-2xl"
            />
          </div>

          {/* Texto */}
          <div className="animate-fadeInUp" style={{ animationDelay: '0.4s' }}>
            <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="fas fa-heart"></i>
              Sobre Nosotros
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-800 mb-4 md:mb-6 leading-tight">
              {content.titlePrefix}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">{content.title}</span>
            </h2>

            <p className="text-lg md:text-xl text-gray-700 mb-6 leading-relaxed">
              {content.description1}
            </p>

            <p className="text-lg md:text-xl text-gray-700 mb-8 leading-relaxed">
              {content.description2}
            </p>

            <div className="space-y-4 md:space-y-6">
              {Array.isArray(content.features) && content.features.map((feature, index) => {
                const text = feature.fields?.text || '';
                const highlight = feature.fields?.highlight || '';

                const [before, after] = text.split(highlight);

                return (
                  <div key={index} className="flex items-start group">
                    <div className="flex-shrink-0 mt-1 w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center transition-all duration-300 group-hover:from-purple-600 group-hover:to-pink-600">
                      <i className="fas fa-check text-purple-600 text-sm md:text-base group-hover:text-white transition-colors duration-300"></i>
                    </div>
                    <p className="ml-4 text-gray-700 text-lg md:text-xl leading-relaxed">
                      {before}
                      <strong className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">{highlight}</strong>
                      {after}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
